import React, { useEffect, useState } from 'react';
import { Modal, View } from 'react-native';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import Toast from 'react-native-toast-message';
import { translate } from '../../../services/translate';
import { WindowInformation } from '../../../services/window-information';
import { dispatchService } from '../../../services/central-api/dispatch';
import { ClientError } from '../../../services/central-api/base-service';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import ActingBodiesDropdown from '../OccurrenceTypes/ActingBodiesDropdown';

interface ModalProps {
    providence?: Providence;
    isModalVisible: boolean;
    authenticatedUser?: AuthenticatedUser;
    onClose: () => void;
}

export default function ProvidenceModal({ onClose, providence, isModalVisible, authenticatedUser }: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [name, setName] = useState(providence?.name || '');
    const [actingBodyId, setActingBodyId] = useState<number>(0);

    function clearValues() {
        setName('');
        setActingBodyId(0);
    }

    function isNameInvalid(value: string) {
        return value == '';
    }

    function isFormValid() {
        return !isNameInvalid(name);
    }

    async function loadPageInfo() {
        if (!authenticatedUser) {
            return;
        }
        if (providence) {
            setName(providence.name);
            setActingBodyId(providence.actingBody.id);
        } else if (!(authenticatedUser.isAdmin || authenticatedUser.permissions.dispatch_manage_all_acting_bodies_providences) && authenticatedUser.actingBodyUnit.actingBodyId) {
            setActingBodyId(authenticatedUser.actingBodyUnit.actingBodyId);
        }
    }

    useEffect(() => {
        if (isModalVisible) {
            loadPageInfo();
        }
    }, [isModalVisible]);

    async function formSubmit() {
        try {
            if (providence?.id) {
                await dispatchService.updateProvidence({
                    id: providence.id,
                    name,
                });
            } else {
                await dispatchService.createProvidence({
                    name,
                    actingBodyId: actingBodyId !== 0 ? actingBodyId : undefined
                });
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            onClose();
            clearValues();
        } catch (err) {
            if (err instanceof ClientError) {
                Toast.show({ type: 'sentinelxError', text1: translate(err.message) });
            } else {
                console.error(err);
                Toast.show({ type: 'sentinelxError', text1: translate('unexpectedError') });
            }
        }
    }

    return (
        <Modal visible={isModalVisible} transparent animationType='fade' onRequestClose={() => {
            onClose();
            clearValues();
        }}>
            <View style={styles.container}>
                <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: '20%' }]}>
                    {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_providences) &&
                        <ActingBodiesDropdown
                            includeAllOption={false}
                            value={actingBodyId}
                            setValue={setActingBodyId}
                            zIndex={2}
                            disabled={!!providence?.id}
                        />
                    }
                    <FormInput
                        label={translate('name')}
                        placeholder={translate('name')}
                        value={name}
                        onChangeText={setName}
                        invalid={isNameInvalid}
                    />
                    <FormActions
                        onSubmit={formSubmit}
                        onClose={() => {
                            onClose();
                            clearValues();
                        }}
                        disabled={!isFormValid()}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet(theme => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: {
        minWidth: 300,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25
    }
}));
